/**
 * Behaviour for the bespoke Alpha sections, replacing the jQuery originals.
 *
 * <custom-accordion> - one-open-at-a-time FAQ list
 * <custom-tabs>      - tabbed product content
 */

class CustomAccordion extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.Custom_faq_content_wrap'));
    if (!this.items.length) return;

    this.items.forEach((item, index) => {
      const trigger = item.querySelector('.Custom_faq_question');
      const panel = item.querySelector('.Custom_faq_answer');
      if (!trigger || !panel) return;

      const panelId = `faq-panel-${this.dataset.sectionId || 'x'}-${index}`;
      panel.id = panelId;
      panel.setAttribute('role', 'region');
      trigger.setAttribute('aria-controls', panelId);
      trigger.setAttribute('aria-expanded', 'false');

      trigger.addEventListener('click', () => this.toggle(item));
    });

    // Collapse everything first. The original markup carried an inline
    // style="display:none" on each answer; that was removed when these became
    // real accordion panels, so without this every row renders expanded.
    this.items.forEach((item) => {
      const panel = item.querySelector('.Custom_faq_answer');
      if (panel) panel.style.height = '0px';
      item.classList.remove('active');
    });

    // Match the previous behaviour: first row open on load.
    if (this.dataset.openFirst !== 'false') this.open(this.items[0]);
  }

  toggle(item) {
    const isOpen = item.classList.contains('active');
    this.items.forEach((other) => this.close(other));
    if (!isOpen) this.open(item);
  }

  open(item) {
    const trigger = item?.querySelector('.Custom_faq_question');
    const panel = item?.querySelector('.Custom_faq_answer');
    if (!panel) return;

    item.classList.add('active');
    trigger?.setAttribute('aria-expanded', 'true');
    panel.style.display = '';

    // Measure at natural height, then animate from 0 to that.
    panel.style.height = 'auto';
    const target = panel.scrollHeight;
    panel.style.height = '0px';
    void panel.offsetHeight; // force reflow so the transition actually runs
    requestAnimationFrame(() => {
      panel.style.height = `${target}px`;
    });

    panel.addEventListener(
      'transitionend',
      () => {
        if (item.classList.contains('active')) panel.style.height = 'auto';
      },
      { once: true }
    );
  }

  close(item) {
    const trigger = item?.querySelector('.Custom_faq_question');
    const panel = item?.querySelector('.Custom_faq_answer');
    if (!panel || !item.classList.contains('active')) return;

    panel.style.height = `${panel.scrollHeight}px`;
    requestAnimationFrame(() => {
      panel.style.height = '0px';
    });

    item.classList.remove('active');
    trigger?.setAttribute('aria-expanded', 'false');
  }
}

class CustomTabs extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('.title_wrap'));
    if (!this.triggers.length) return;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.select(trigger));
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.select(trigger);
        }
      });
    });
  }

  select(trigger) {
    const target = trigger.dataset.target;
    if (!target) return;

    this.triggers.forEach((other) => {
      const active = other === trigger;
      other.classList.toggle('active', active);
      other.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.querySelectorAll('.product_tabs_content_block').forEach((panel) => {
      panel.classList.toggle('active', panel.id === target);
    });
  }
}

customElements.define('custom-accordion', CustomAccordion);
customElements.define('custom-tabs', CustomTabs);
